import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { intl } from '@trello/i18n';
import { Icon } from '@trello/nachos/icon';
import { CheckIcon } from '@trello/nachos/icons/check';
import { DownIcon } from '@trello/nachos/icons/down';
import { Popover, usePopover } from '@trello/nachos/popover';
import { PopoverMenu, PopoverMenuButton } from '@trello/nachos/popover-menu';
import { useSharedState } from '@trello/shared-state';

import { getBoardSwitcherSource } from './getBoardSwitcherSource';
import { useBoardSwitcher } from './useBoardSwitcher';
import { WorkspaceDisplayName } from './WorkspaceDisplayName';

import * as styles from './WorkspaceTabsMenuButton.module.less';

const MaybeCheckIcon = ({ condition }: { condition: boolean }) => {
  if (condition) {
    return <CheckIcon size="small" />;
  }
  return (
    <Icon
      size="small"
      glyph={() => <span />}
      dangerous_className={styles.blankIcon}
    />
  );
};

export const WorkspaceTabsMenuButton: FunctionComponent = () => {
  const { memberWorkspaceIds, guestWorkspaceIds } = useBoardSwitcher();
  const [{ selectedWorkspaceId }] = useSharedState(boardSwitcherSharedState);

  const { hide, toggle, triggerRef, popoverProps } = usePopover({
    placement: 'bottom-end',
  });

  const handleOpenPopover = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'workspaceTabsMenuButton',
      source: getBoardSwitcherSource(),
    });

    toggle();
  }, [toggle]);

  const onClick = useCallback(
    (id: string | null) => {
      Analytics.sendClickedButtonEvent({
        buttonName: 'workspaceTabsMenuItemButton',
        source: getBoardSwitcherSource(),
        attributes: {
          workspaceFilter: id ?? 'all',
        },
      });

      hide();
      boardSwitcherSharedState.setValue({
        selectedWorkspaceId: id,
      });
    },
    [hide],
  );

  return (
    <>
      <button
        title={intl.formatMessage({
          id: 'templates.board_switcher.select-a-workspace',
          defaultMessage: 'Select a Workspace',
          description:
            'Title on button that opens a popover to select a workspace on the board switcher',
        })}
        className={styles.menuButton}
        ref={triggerRef}
        onClick={handleOpenPopover}
      >
        <DownIcon size="small" />
      </button>
      <Popover {...popoverProps}>
        <PopoverMenu>
          <PopoverMenuButton
            onClick={() => onClick(null)}
            iconBefore={<MaybeCheckIcon condition={!selectedWorkspaceId} />}
          >
            <FormattedMessage
              id="templates.board_switcher.all"
              defaultMessage="All"
              description="Text on the all workspaces button on the workspace popover menu"
            />
          </PopoverMenuButton>
          {memberWorkspaceIds.map((id) => (
            <PopoverMenuButton
              key={id}
              onClick={() => onClick(id)}
              iconBefore={
                <MaybeCheckIcon condition={selectedWorkspaceId === id} />
              }
            >
              <WorkspaceDisplayName workspaceId={id} />
            </PopoverMenuButton>
          ))}
          {guestWorkspaceIds.map((id) => (
            <PopoverMenuButton
              key={id}
              onClick={() => onClick(id)}
              iconBefore={
                <MaybeCheckIcon condition={selectedWorkspaceId === id} />
              }
            >
              <WorkspaceDisplayName workspaceId={id} />
            </PopoverMenuButton>
          ))}
        </PopoverMenu>
      </Popover>
    </>
  );
};
